import { useFeatureValue } from '@growthbook/growthbook-react';
import { Add, ArrowBackIosNew, VerticalAlignBottom } from '@mui/icons-material';
import { Box, Menu, Typography, useTheme } from '@mui/material';
import { useMediaQuery } from '@mui/system';
import { useMemo, useRef, useState } from 'react';

import { Button, ButtonVariants } from '@/components';
import { Modals } from '@/constants';
import { exportCsv, exportZip } from '@/images';
import { AnalyticsService, EVENTS } from '@/services';
import {
  downloadCertificateSummary,
  selectUserCompliance,
  uiActions,
  useAppDispatch,
  useAppSelector,
  useFetchCreditsQuery,
} from '@/store';
import { appStyles } from '@/theme';
import {
  formatTrackerExportFileName,
  rowsToInlineCsv,
  transformTrackerDataToCsv,
} from '@/utils';

import {
  CertificatesTable,
  CertificatesTableOld,
  Filters,
  FiltersOld,
  Header,
  PinnedFieldsOfStudy,
  TriennialSummary,
} from './components';
import { filterCredits } from './utils';

export const Tracker = () => {
  const exportRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();

  const trackerUpdatesV1 = useFeatureValue('tracker-updates-v1', false);

  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<[Date, Date]>([
    new Date(new Date().getFullYear(), 0, 1),
    new Date(),
  ]);
  const [selectedFilter, setSelectedFilter] = useState<
    'all' | 'lumiq' | 'custom'
  >('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);

  const { data: credits } = useFetchCreditsQuery({
    dateRange: {
      fromDate: selectedDateRange[0].toISOString(),
      toDate: selectedDateRange[1].toISOString(),
    },
  });
  const compliance = useAppSelector(selectUserCompliance);

  const filteredCertificates = useMemo(() => {
    if (!credits) return [];
    return filterCredits({
      credits,
      selectedFilter,
      searchTerm,
    });
  }, [credits, selectedFilter, searchTerm]);

  const csvData = useMemo(() => {
    const rowData = transformTrackerDataToCsv(
      filteredCertificates ?? [],
      compliance,
    );

    return rowsToInlineCsv(rowData);
  }, [filteredCertificates, compliance]);

  return (
    <Box sx={[appStyles.limitPageWidth]}>
      <Header
        selectedDateRange={selectedDateRange}
        setSelectedDateRange={setSelectedDateRange}
        credits={credits}
      />
      <TriennialSummary />
      <PinnedFieldsOfStudy credits={credits ?? []} />
      <Typography variant="h2" sx={{ mt: 6 }}>
        My Certificates
      </Typography>
      <Box
        sx={{
          width: '100%',
          mt: 2,
          mb: 3,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}>
        {trackerUpdatesV1 ? (
          <Filters
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            setSearchTerm={setSearchTerm}
          />
        ) : (
          <FiltersOld
            credits={credits}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            setSearchTerm={setSearchTerm}
          />
        )}
        {!isMobile && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                height: 45,
              }}>
              <Button
                label="Add External Certificate"
                fullWidth={false}
                skinny={!trackerUpdatesV1}
                variant={ButtonVariants.PRIMARY}
                startIcon={<Add />}
                onClick={() => {
                  dispatch(
                    uiActions.setActiveModal({
                      name: Modals.TRACKER_ADD_EDIT,
                    }),
                  );
                }}
              />
              <Button
                ref={exportRef}
                label="Export"
                skinny={!trackerUpdatesV1}
                variant={ButtonVariants.SECONDARY}
                colorOverride={'secondary.main'}
                sx={{ ml: 2 }}
                startIcon={
                  <VerticalAlignBottom sx={{ color: 'secondary.main' }} />
                }
                endIcon={
                  <Box
                    sx={{
                      transform: 'rotate(-90deg)',
                    }}>
                    <ArrowBackIosNew
                      sx={{ width: 13, height: 13, color: 'grey150' }}
                    />
                  </Box>
                }
                onClick={() => {
                  setIsExportMenuOpen(true);
                }}
                disabled={
                  isDownloading || (filteredCertificates?.length ?? 0) === 0
                }
                isLoading={isDownloading}
              />
              <Menu
                anchorEl={exportRef.current}
                open={isExportMenuOpen}
                onClose={() => setIsExportMenuOpen(false)}
                sx={{
                  '& .MuiPaper-root': {
                    p: 2,
                    ...appStyles.popoverPaper,
                  },
                  '& .MuiMenu-list': {
                    p: 0,
                  },
                }}
                transformOrigin={{
                  vertical: -8,
                  horizontal: 'left',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2,
                    p: 1,
                    mb: 1,
                    '&:hover': {
                      bgcolor: theme => `${theme.palette.secondary.medium}59`,
                    },
                    width: 280,
                    borderRadius: 2,
                    cursor: 'pointer',
                    userSelect: 'none',
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                  component={'a'}
                  href={csvData}
                  // format date as yyyyMMdd
                  download={formatTrackerExportFileName({
                    fromDate: selectedDateRange[0].toISOString(),
                    toDate: selectedDateRange[1].toISOString(),
                    extension: 'csv',
                  })}
                  onClick={() => {
                    setIsExportMenuOpen(false);
                    AnalyticsService.logEvent(
                      EVENTS.TRACKER_EVENTS.CERTIFICATES_EXPORTED,
                      {
                        export_type: 'CSV',
                        source_filter:
                          selectedFilter === 'all'
                            ? 'All'
                            : selectedFilter === 'lumiq'
                              ? 'lumiq'
                              : 'external',
                        record_count: filteredCertificates?.length ?? 0,
                      },
                    );
                  }}>
                  <Box
                    sx={{
                      p: '9px 6px',
                      bgcolor: 'secondary.medium',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 'fit-content',
                    }}>
                    <Box
                      component="img"
                      src={exportCsv}
                      sx={{ width: 16, height: 16 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body3SemiBold">
                      Data Export (CSV)
                    </Typography>
                    <Typography variant="caption1" sx={{ mt: 0.5 }}>
                      Download a CSV report of all your course details and
                      earned credits in one centralized place.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <VerticalAlignBottom
                      sx={{ color: 'grey150', width: 20, height: 20 }}
                    />
                  </Box>
                </Box>
                <Box
                  onClick={async () => {
                    setIsDownloading(true);
                    setIsExportMenuOpen(false);
                    try {
                      await dispatch(
                        downloadCertificateSummary({
                          fromDate: selectedDateRange[0].toISOString(),
                          toDate: selectedDateRange[1].toISOString(),
                        }),
                      );
                      AnalyticsService.logEvent(
                        EVENTS.TRACKER_EVENTS.CERTIFICATES_EXPORTED,
                        {
                          export_type: 'Certificates',
                          source_filter:
                            selectedFilter === 'all'
                              ? 'All'
                              : selectedFilter === 'lumiq'
                                ? 'lumiq'
                                : 'external',
                          record_count: filteredCertificates?.length ?? 0,
                        },
                      );
                    } finally {
                      setIsDownloading(false);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2,
                    p: 1,
                    width: 280,
                    borderRadius: 2,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                      bgcolor: theme => `${theme.palette.secondary.medium}59`,
                    },
                  }}>
                  <Box
                    sx={{
                      p: '9px 6px',
                      bgcolor: 'secondary.medium',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 'fit-content',
                    }}>
                    <Box
                      component="img"
                      src={exportZip}
                      sx={{ width: 16, height: 16 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body3SemiBold">
                      Export Certificates (PDF)
                    </Typography>
                    <Typography variant="caption1" sx={{ mt: 0.5 }}>
                      A zipped bundle of your certificate PDF files for easy
                      record-keeping.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <VerticalAlignBottom
                      sx={{ color: 'grey150', width: 20, height: 20 }}
                    />
                  </Box>
                </Box>
              </Menu>
            </Box>
          </Box>
        )}
      </Box>
      {trackerUpdatesV1 && (
        <Box
          sx={{
            my: 2.5,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}>
          <Typography variant="body2">Total of&nbsp;</Typography>
          <Typography variant="body2SemiBold">
            {filteredCertificates?.length ?? 0}
          </Typography>
          <Typography variant="body2">&nbsp;certificates</Typography>
          <Box
            sx={{
              mx: 1,
              width: 5,
              height: 5,
              borderRadius: '50%',
              bgcolor: 'secondary.border',
            }}
          />
          <Typography variant="body2">
            {Math.round(
              (filteredCertificates?.reduce(
                (acc, cert) => acc + cert.credit,
                0,
              ) ?? 0) * 100,
            ) / 100}
            &nbsp;{compliance === 'CPD' ? 'CPD Hours' : 'CPE Credits'}
          </Typography>
        </Box>
      )}
      {trackerUpdatesV1 ? (
        <CertificatesTable
          certificates={filteredCertificates ?? []}
          filters={{ type: selectedFilter, searchTerm }}
        />
      ) : (
        <CertificatesTableOld
          certificates={filteredCertificates ?? []}
          filters={{ type: selectedFilter, searchTerm }}
        />
      )}
    </Box>
  );
};
